
    import Component from "vue-class-component";
    import Vue from "vue";
    import { BvTableFieldArray } from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
    import {Prop} from "vue-property-decorator";
    import {NavigationGuardNext} from "vue-router/types/router";
    import toasts from "@/components/Toasts";
    import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
    import {debounceFn} from "debounce-decorator-ts";
    import {SysAdminCertificateTypeTemplatesClient, CertificateTypeTemplateModel} from "@/api-client";

    interface Props {
        preloadedTemplates: CertificateTypeTemplateModel[];
    }

    @Component({
        components: {
            TableEmptySlot,
            HeadingWave,
            SectionPageHeading,
            ButtonWithConfirm
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<CertificateTypeTemplates>) {
            const props = to.meta!.props as Props;
            const client = new SysAdminCertificateTypeTemplatesClient();
            client.list()
                .then(templates => {
                    props.preloadedTemplates = templates;
                    next();
                })
                .catch(() => {
                    props.preloadedTemplates = [];
                    next();
                });
        }
    })
    export default class CertificateTypeTemplates extends Vue implements Props {
        @Prop({ required: true })
        preloadedTemplates!: CertificateTypeTemplateModel[];

        templates = this.preloadedTemplates;
        filteredTemplates = this.templates;

        searchPhrase = "";
        initialSort = { sortBy: "name", sortDesc: false };

        created() {
            this.getParametersFromRouteOrDefault();

            this.$watch(() => this.searchPhrase, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.$route.query, () => {
                this.search();
            }, { deep: true, immediate: true });
        }

        search(){
            this.filteredTemplates = this.templates.filter(o =>
                o.name.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0 ||
                o.certificateTypeDisplayName.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0 ||
                o.certificateTypeNames.some(n => n.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0)
            );
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchPhrase: this.searchPhrase || undefined
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const query = this.$route.query;
            this.searchPhrase = typeof query.searchPhrase === 'string' ? query.searchPhrase : '';
        }

        navigateToDetails(template: CertificateTypeTemplateModel) {
            this.$router.push({
                name: "certificate-type-template",
                params: { id: template.id },
            });
        }

        async deactivate(template: CertificateTypeTemplateModel) {
            const client = new SysAdminCertificateTypeTemplatesClient();
            await client.deactivate(template.id);
            template.isActive = false;
            toasts.success("Certificate type template has been deactivated");
        }

        async activate(template: CertificateTypeTemplateModel) {
            const client = new SysAdminCertificateTypeTemplatesClient();
            await client.activate(template.id);
            template.isActive = true;
            toasts.success("Certificate type template has been activated");
        }

        fields: BvTableFieldArray = [
            { key: "name", label: this.$ls.Global_Label_Name, sortable: true, thClass: "w-30" },
            { key: "certificateTypeDisplayName", label: this.$ls.Administration_CertificateTypeTemplates_Label_CertificateType, sortable: true, thClass: "w-30",
                formatter: (value: string) => {
                    // Check if it's the special multiple format
                    if (value && value.startsWith("__MULTIPLE__")) {
                        const count = value.replace("__MULTIPLE__", "");
                        return `${this.$ls.Global_Text_Multiple} (${count})`;
                    }
                    return value || "";
                }
            },
            { key: "isActive", label: this.$ls.Global_Label_Active, sortable: false, formatter: (value: boolean, key: string, item: CertificateTypeTemplateModel): string => translateTrueFalse(item.isActive) },
            { key: "actions", label: "", headerTitle: "actions", sortable: false, tdClass: "table-actions" },
            { key: "linkIcon", label: "", sortable: false, tdClass:"table-actions" }
        ];
    }
